import { HttpClient } from "@angular/common/http";
import { EventEmitter, Inject, Injectable, Output } from "@angular/core";
import { BehaviorSubject, catchError, delay, dematerialize, finalize, firstValueFrom, last, lastValueFrom, map, materialize, Observable, of, take, tap } from "rxjs";
import { TaskAgenda } from "../api/tasks/my-tasks/my-tasks.state";
import { isNullOrUndefined, isNullOrWhitespace } from "../common/functions";
import { BuildConfigService } from "./build-config.service";
import { Honeycomb } from "./honeycomb-api/honeycomb-api";
import FilterInit, { TaskSearchProperties } from "./tasks-filter";
import { AgendaLoading, TaskPageResult } from "./tasks-page-resutl";

@Injectable({
    providedIn: 'root'
})

export class TasksService {

    public initialized = false;

    public loading: BehaviorSubject<AgendaLoading> = new BehaviorSubject({ agendaKey: null, loading: false });
    
    private callIndex: number = 0; // for synchronization possible parallel calls

    public jobIdChangeEvent: number = null;

    @Output()
    public data: EventEmitter<TaskPageResult> = new EventEmitter<TaskPageResult>();

    @Output()
    public total: EventEmitter<number> = new EventEmitter<number>();

    @Output()
    public loadingData: EventEmitter<AgendaLoading> = new EventEmitter<AgendaLoading>();

    @Output()
    public forcePagerUpdate: EventEmitter<void> = new EventEmitter();

    public totalStatic: number = 0;

    private operationsAll: Array<Honeycomb.Tenant.Tasker.IService.Model.Operation> = null;

    private taskStatuses: Array<Honeycomb.Tenant.Tasker.IService.Model.TaskStatus> = null;

    private lastAgenda: TaskAgenda = TaskAgenda.Dashboard;

    private preserveLoadedFilterAgenda: TaskAgenda | null = null;

    public pageSize(agendaKey?: string): number {

        console.log('pageSize', agendaKey, this.taskSearchProperties.pageSizeMap.get(agendaKey));

        if (!isNullOrWhitespace(agendaKey)) {
            return this.taskSearchProperties.pageSizeMap.get(agendaKey);
        }
        return this.taskSearchProperties.listFilter.pageSize || 10;
    }

    public pageIndex(agendaKey?: string): number {

        console.log('pageIndex', agendaKey, this.taskSearchProperties.pageIndexMap.get(agendaKey));

        if (!isNullOrWhitespace(agendaKey)) {
            return this.taskSearchProperties.pageIndexMap.get(agendaKey);
        }
        return this.taskSearchProperties.listFilter.pageIndex || 10;
    }

    public operationsGet(agendaKey: string): Array<number> {

        console.log('operations', agendaKey, this.taskSearchProperties.operationsMap.get(agendaKey));
        return this.taskSearchProperties.operationsMap.get(agendaKey);
    }

    private taskSearchProperties: TaskSearchProperties;

    constructor(
        private http: HttpClient,
        @Inject('UserController')
        private userController: Honeycomb.Tenant.Contact.IService.UserController,
        @Inject('TaskController')
        private taskController: Honeycomb.Tenant.Tasker.IService.Controller.TaskController,
        @Inject('OperationController') private operationController: Honeycomb.Tenant.Tasker.IService.Controller.OperationController,
        @Inject('TaskStatusController') private taskStatusController: Honeycomb.Tenant.Tasker.IService.Controller.TaskStatusController,
        @Inject('ConfigController') private configController: Honeycomb.Tenant.Admin.IService.NamedConfigurationsController,
        private buildConfig: BuildConfigService
        ) {
        this.taskSearchProperties = FilterInit();
    }

    private async loadOperations() {
        this.operationsAll = await firstValueFrom(this.operationController.ActiveList(null));
        return this.operationsAll;
    }

    public async setDefaultAgendaFilter(reset?: boolean, agenda?: TaskAgenda, jobID?: number) {
        const preserveLoadedFilter = !isNullOrUndefined(agenda) && this.preserveLoadedFilterAgenda === agenda;

        if (isNullOrUndefined(agenda) || (!preserveLoadedFilter && (reset || this.lastAgenda !== agenda))) {
            this.taskSearchProperties.listFilter = new Honeycomb.Tenant.Tasker.IService.TaskListFilter();
        }
        if (!isNullOrUndefined(agenda) && this.lastAgenda !== agenda && agenda !== TaskAgenda.Calendar) {
            await this.setDefaultAttributesByAgenda(agenda);
            await this.setDefaultOperationsByAgenda(agenda);
        }
        if (!!jobID && this.lastAgenda !== agenda && agenda !== TaskAgenda.Calendar && !preserveLoadedFilter) {
            await this.setDefaultTaskStatuses(jobID);
        }
        if (reset && !preserveLoadedFilter) {
            var sortResponse = await lastValueFrom(this.configController.GetByNames(['tasker.tasks.additional-settings']));
            var sortConfig = sortResponse?.['tasker.tasks.additional-settings']?.ordering;
            this.setSort(sortConfig?.sort || "taskID", sortConfig?.direction || "desc", sortConfig?.attribute || false);
        }
        if (agenda == TaskAgenda.Calendar) {
            this.setSort('dateFrom', 'asc');
        }

        if (preserveLoadedFilter) {
            this.preserveLoadedFilterAgenda = null;
        }
    }

    public async setDefaultOperationsByAgenda(agenda: TaskAgenda) {

        let agendasPrefixes = ['tasks-main-list-', 'tasks-main-table-'];
        let agendaKeys = agendasPrefixes.map(p => p + agenda);

        if (!this.operationsAll) {
            await this.loadOperations();
        }
        if (agenda !== TaskAgenda.Repairs) {
            var preselectedOperations = this.operationsAll.filter(p => p.taskType !== Honeycomb.Common.Enums.TaskType.repairRequest);
            agendaKeys.forEach(a => { 
                this.taskSearchProperties.operationsMap.set(a, preselectedOperations.map(o => o.operationID));
            })
        }
        else {
           var  preselectedOperations = this.operationsAll.filter(p => p.taskType === Honeycomb.Common.Enums.TaskType.repairRequest);
            agendaKeys.forEach(a => { 
                this.taskSearchProperties.operationsMap.set(a, preselectedOperations.map(o => o.operationID));
            })
        }
    }

    public async setDefaultAttributesByAgenda(agenda: TaskAgenda) {

        let filter = this.taskSearchProperties.listFilter
        if (agenda === TaskAgenda.Repairs) {
            filter.includeAttributes = this.buildConfig.taskList.repair.attributes;
        } else {
            filter.includeAttributes = this.buildConfig.taskList.default.attributes;
        }
    }

    public async setDefaultTaskStatuses(selectedJobID: number) {
        this.taskSearchProperties.listFilter.jobID = selectedJobID;
        this.taskStatuses = await firstValueFrom(this.taskStatusController.List(null, selectedJobID));
        let filter = this.taskSearchProperties.listFilter;
        filter.taskStatusIDs = this.taskStatuses.filter(s => s.defaultFilter).map(a => a.taskStatusID);
    }

    public loadPage(agendaKey?: string): Observable<TaskPageResult> {
        console.log('loadPage', agendaKey);

        this.lastAgenda = this.taskSearchProperties.agenda;

        if (!this.taskSearchProperties.listFilter.taskRelationAdditional) {
            this.taskSearchProperties.listFilter.taskRelationAdditional = [];
        }

        if (this.jobIdChangeEvent) {
            this.taskSearchProperties.listFilter.jobID = this.jobIdChangeEvent;
        }

        let operationIDs = this.taskSearchProperties.operationsMap.get(agendaKey);
        if (this.taskSearchProperties.listFilter.operations?.length) {
            operationIDs = this.taskSearchProperties.listFilter.operations;
        }

        const filter: Honeycomb.Tenant.Tasker.IService.Model.TaskPage = {
            userID: -1,
            jobID: this.taskSearchProperties.listFilter.jobID,
            pageIndex: this.taskSearchProperties.pageIndexMap.get(agendaKey) ?? 0,
            pageSize: this.taskSearchProperties.pageSizeMap.get(agendaKey) ?? 10,
            filter: {
            fulltext: this.taskSearchProperties.listFilter.fulltext,
            assignedUsers: this.taskSearchProperties.listFilter.taskRelationAdditional
                .filter(r => r.taskRelation === Honeycomb.Common.Enums.TaskRelation.assignee)
                .map(r => r.userID),
            assignerUsers: this.taskSearchProperties.listFilter.taskRelationAdditional
                .filter(r => r.taskRelation === Honeycomb.Common.Enums.TaskRelation.assigner)
                .map(r => r.userID),
            creatorUsers: this.taskSearchProperties.listFilter.taskRelationAdditional
                .filter(r => r.taskRelation === Honeycomb.Common.Enums.TaskRelation.creator)
                .map(r => r.userID),
            name: null,
            description: null,
            mainFilter: this.taskSearchProperties.listFilter.mainFilter,
            locationIDs: this.taskSearchProperties.listFilter.locations,
            operationIDs,
            priorities: this.taskSearchProperties.listFilter.priorities,
            taskStatusIDs: this.taskSearchProperties.listFilter.taskStatusIDs,
            dateFrom: this.taskSearchProperties.listFilter.dateFrom,
            dateTo: this.taskSearchProperties.listFilter.dateTo,
            taskRead:
                this.taskSearchProperties.listFilter.taskRead === Honeycomb.Common.Enums.TaskRead.read
                ? true
                : this.taskSearchProperties.listFilter.taskRead === Honeycomb.Common.Enums.TaskRead.notRead
                ? false
                : null
            } as Honeycomb.Tenant.Tasker.IService.Model.TaskPageFilter,
            sort: this.taskSearchProperties.sort
        };

        console.log('PageRequest - filter', filter, agendaKey);

        return this.taskController.Page(filter).pipe(
            map(res => ({
                agendaKey: agendaKey ?? '',
                pageIndex: res.pageIndex ?? 0,
                pageSize: res.pageSize ?? (this.pageSize(agendaKey) ?? 10),
                total: res.total ?? 0,
                results: (res.results ?? []).map(t => t as Honeycomb.Tenant.Tasker.IService.Model.Task)
            }) as TaskPageResult),
            tap(pr => {
            console.log(
                'PageResult',
                agendaKey,
                pr.agendaKey,
                pr.results.map(t => t.operation.taskType)
            );
            }),
            catchError(error => {
            console.error('task-service', error);
            const emptyResult: TaskPageResult = {
                agendaKey: agendaKey ?? '',
                pageIndex: 0,
                pageSize: this.pageSize(agendaKey) ?? 10,
                total: 0,
                results: []
            };
            return of(emptyResult);
            })
        );
    }

    public load(agendaKey?: string, useMutipleCalls?: boolean, callIndex?: number): Promise<void> {
        this.loadingData.emit({ agendaKey, loading: true });
        this.callIndex = callIndex ?? this.callIndex;
        this.loading.next({ agendaKey, loading: true });

        let lastAKey = agendaKey;

        const stream$ = this.loadPage(agendaKey).pipe(
            take(1),
            tap(pageResult => {
            lastAKey = pageResult.agendaKey || agendaKey || lastAKey;

            if (pageResult.total === 0) {
                const pageIndex = this.taskSearchProperties.listFilter.pageIndex ?? 0;
                const pageSize  = this.taskSearchProperties.listFilter.pageSize ?? 10;

                let newTotal = pageIndex * pageSize + pageResult.results.length;

                const expected = this.taskSearchProperties.pageSizeMap.get(lastAKey) ?? pageSize;
                if (lastAKey && pageResult.results.length === expected) {
                newTotal = (pageIndex * pageSize) + 2 * pageSize;
                this.total.next(newTotal);
                } else if (pageResult.results.length < pageSize) {
                newTotal = pageIndex * pageSize + pageResult.results.length;
                }

                pageResult.total = newTotal;
                this.totalStatic = newTotal;
            }

            if (useMutipleCalls && this.callIndex > (callIndex ?? 0)) {
                return;
            }

            this.totalStatic = pageResult.total;
            this.total.next(pageResult.total);

            this.taskSearchProperties.results = pageResult.results;

            const out: TaskPageResult = {
                agendaKey: pageResult.agendaKey,
                results: pageResult.results ?? [],
                total: pageResult.total ?? 0,
                pageIndex: pageResult.pageIndex ?? (this.taskSearchProperties.listFilter.pageIndex ?? 0),
                pageSize: pageResult.pageSize ?? (this.taskSearchProperties.listFilter.pageSize ?? 10)
            };
            this.data.emit(out);
            }),
            finalize(() => {
                this.loading.next({ agendaKey: lastAKey, loading: false });
                this.loadingData.emit({ agendaKey: lastAKey, loading: false });
            }),
            map(() => void 0)
        );

        return firstValueFrom(stream$);
    }


    public setPager = (agendaKey: string, pageIndex: number, pageSize?: number) => {

        console.log(`agendaKey: ${agendaKey}, pageIndex: ${pageIndex}, pageSize: ${pageSize}`);

        if (!isNullOrUndefined(pageSize)) {
            this.taskSearchProperties.pageSizeMap.set(agendaKey, pageSize);
        } else {
            pageSize = this.taskSearchProperties.pageSizeMap.get(agendaKey) || 10;
        }

        if (!isNullOrUndefined(pageIndex)) {
            this.taskSearchProperties.pageIndexMap.set(agendaKey, pageIndex);
        } else {
            pageIndex = this.taskSearchProperties.pageIndexMap.get(agendaKey) || 0;
        }
      
        if (!isNullOrUndefined(pageSize)) {
            this.taskSearchProperties.listFilter.offset = pageSize * pageIndex;
            this.taskSearchProperties.listFilter.limit = pageSize;
            this.taskSearchProperties.listFilter.pageSize = pageSize;
        }
    }

    public setSort(sortColumn: string, direction: string, isAttribute?: boolean) {
        console.log(`Sort by: ${sortColumn}`);
        this.taskSearchProperties.sort.column = sortColumn;
        this.taskSearchProperties.sort.direction = direction;
        this.taskSearchProperties.sort.isAttribute = isAttribute || false;
    }

    public getSort(): Honeycomb.Tenant.Tasker.IService.Model.TaskPageSort
    {
        return Object.assign({}, this.taskSearchProperties.sort);
    }

    public async setFilter(filter: Honeycomb.Tenant.Tasker.IService.TaskListFilter, agenda: TaskAgenda, saveFilter: boolean) {
        var lf = this.taskSearchProperties.listFilter;
        lf.taskRelationAdditional = filter.taskRelationAdditional;
        lf.userTaskRelations = filter.userTaskRelations;
        lf.includeAttributes = filter.includeAttributes;
        lf.mainFilter = filter.mainFilter;
        lf.jobID = filter.jobID;
        lf.locations = filter.locations;
        lf.locationID = filter.locationID;
        lf.taskSequenceID = filter.taskSequenceID;
        lf.baseTaskStatuses = filter.baseTaskStatuses;
        lf.taskRead = filter.taskRead;
        lf.taskStatusIDs = filter.taskStatusIDs;
        lf.taskTypes = filter.taskTypes;
        lf.dateFrom = this.toDate(filter.dateFrom);
        lf.dateTo = this.toDate(filter.dateTo);
        lf.priority = filter.priority;
        lf.priorities = filter.priorities;

        if (!isNullOrUndefined(filter.pageIndex) && !isNullOrUndefined(filter.pageSize)) {
            lf.pageIndex = filter.pageIndex;
            lf.pageSize = filter.pageSize;
        }
        if (!lf.pageIndex) {
            lf.pageIndex = 0;
        }

        if (!lf.pageSize) {
            lf.pageSize = 10;
        }

        lf.fulltext = filter.fulltext;
        lf.operations = filter.operations;
        lf.sortParams = filter.sortParams;

        if (agenda == TaskAgenda.Calendar) {
            this.setSort('dateFrom', 'asc');
        }

        if(saveFilter){
            this.userController.GetCurrentUserInfo().subscribe(res => {
                let attributes= new Array<Honeycomb.Tenant.Contact.IService.UserAttributeNameValue>;

                let attribute = new Honeycomb.Tenant.Contact.IService.UserAttributeNameValue();
                attribute.userId = res.userId;
                attribute.attrName = `TaskerFilter${TaskAgenda[agenda]}`;
                attribute.attrValue = JSON.stringify(lf);

                console.log(attribute);
                attributes.push(attribute);


                this.userController.UpdateAttributes(res.userId, attributes).subscribe();
            });
        }
    }

    public async loadFilter(agenda: TaskAgenda): Promise<boolean> {
        const attributeName = `TaskerFilter${TaskAgenda[agenda]}`;

        try {
            const user = await firstValueFrom(this.userController.GetCurrentUserInfo());
            const res  = await firstValueFrom(this.userController.GetAttributeValue(user.userId, attributeName));
            const filter = this.parseStoredFilter(res);

            if (filter) {
                await this.setFilter(filter, agenda, false);
                this.preserveLoadedFilterAgenda = agenda;
                return true;
            }
        } catch (err) {
            console.warn('loadFilter failed', err);
        }

        return false;
    }
    // public async loadFilter(agenda: TaskAgenda): Promise<void>{
    //     //if(this.buildConfig.taskList.saveFilterSettings){
    //         let attributeName = `TaskerFilter${TaskAgenda[agenda]}`;
    //         this.userController.GetCurrentUserInfo().subscribe(res => {
    //             this.userController.GetAttributeValue(res.userId, attributeName).subscribe(res => {
    //                 const filter = res as Honeycomb.Tenant.Tasker.IService.TaskListFilter;//JSON.parse(res) as Honeycomb.Tenant.Tasker.IService.TaskListFilter;
    //                 if(filter != null){
    //                     this.setFilter(filter, agenda, false);
    //                 }
    //             });
    //         });
    //     //}
    // }

    private toDate(val: unknown): Date | null {
        if (!val) return null;

        if (val instanceof Date) return new Date(val.getTime());

        if (typeof val === 'string') {
        const d = new Date(val);
        if (isNaN(d.getTime())) return null;
        return d;
        }

        if (typeof (val as any)?.toDate === 'function') {
        return (val as any).toDate();
        }

        if (typeof (val as any)?.isValid === 'function' && typeof (val as any)?.toDate === 'function') {
        return (val as any).toDate();
        }

        return null;
    }

    private parseStoredFilter(value: unknown): Honeycomb.Tenant.Tasker.IService.TaskListFilter | null {
        if (!value) {
            return null;
        }

        if (typeof value === 'string') {
            try {
                return JSON.parse(value) as Honeycomb.Tenant.Tasker.IService.TaskListFilter;
            } catch {
                return null;
            }
        }

        if (typeof value === 'object') {
            const attrValue = (value as Honeycomb.Tenant.Contact.IService.UserAttributeNameValue)?.attrValue;

            if (typeof attrValue === 'string') {
                try {
                    return JSON.parse(attrValue) as Honeycomb.Tenant.Tasker.IService.TaskListFilter;
                } catch {
                    return null;
                }
            }

            return value as Honeycomb.Tenant.Tasker.IService.TaskListFilter;
        }

        return null;
    }

    public getFilterRef(): Honeycomb.Tenant.Tasker.IService.TaskListFilter {
        return this.taskSearchProperties.listFilter;
    }

    public getFilterCopy(): Honeycomb.Tenant.Tasker.IService.TaskListFilter {
        var res =  Object.assign({}, this.taskSearchProperties.listFilter);
        return res;
    }
}